import { defineStore } from 'pinia'
import { nextTick } from 'vue'

import constants from '@/constants'
import getI18n from '@/logic/i18nInstance'
import learningmaterials from '@/logic/learningmaterials'
import { relaunchApi } from '@/logic/relaunchApi'
import azureChatApi from '@/modules/chat/api/azureChat'
import chatApi from '@/modules/chat/api/chat'
import { useChatStore } from '@/modules/chat/chatStore'
import { useChatThreadStore } from '@/modules/chat/chatThreadStore'
import { getParticipant } from '@/modules/chat/logic/chatThread'
import { getImageThumbBase64 } from '@/modules/chat/logic/image'
import { detectLanguage } from '@/modules/global/langDetection'
import { extractVolumeProfileFromBlob, extractAudioDurationFromBlob } from '@/modules/global/logic/audioVolumeAnalyzer'

export const useChatAttachmentsStore = (azureChatThreadId) => {
  if (!azureChatThreadId) {
    throw new Error('azureChatThreadId is required')
  }
  const chatThreadStore = useChatThreadStore(azureChatThreadId)
  const chatStore = useChatStore()
  const attachments = localStorage.getItem('chat_attachments')
  const store = defineStore(`chatAttachmentsStore-${azureChatThreadId}`, {
    state: () => ({
      azureChatThreadId,
      attachments: attachments ? JSON.parse(attachments) : {},
      loadingMessages: false,
      isUploading: false,
      chatThread: chatStore.myChatThreads.find((thread) => thread.azure_chat_thread_id === azureChatThreadId),
    }),
    actions: {
      async handleFailureWhenUploading(attachmentId, errorStatus) {
        setTimeout(async () => {
          const messageId = this.attachments[attachmentId]
          if (!messageId) {
            return
          }

          const updatedMessage = chatThreadStore.updateMessageByAzureId(messageId, {
            metadata: {
              state: 'error',
              error_status: errorStatus,
              file_url: undefined,
              uploading: 'false',
            },
          })

          if (updatedMessage) {
            await azureChatApi.updateMessage(
              azureChatThreadId,
              messageId,
              { metadata: updatedMessage.metadata },
            )

            this.isUploading = false
          }
        })
      },
      async handleSendAttachmentMessage(file) {
        this.isUploading = true
        try {
          // Normalize MIME (strip codecs) to improve detection
          const normalizedMime = (file.fileType || '').split(';')[0]
          let fileType = learningmaterials.getType({ file_type: normalizedMime })
          if (normalizedMime.startsWith('video/webm') || normalizedMime === 'video/mp4') {
            fileType = constants.LEARNINGMATERIALS.TYPE_VIDEO
          }
          if (normalizedMime === 'image/webp') {
            fileType = constants.LEARNINGMATERIALS.TYPE_DOCUMENT_IMAGE
          }
          // Treat any audio/* as audio to support webm/ogg recordings
          if (normalizedMime.startsWith('audio/')) {
            fileType = constants.LEARNINGMATERIALS.TYPE_AUDIO
          }

          const metadata = {
            file_id: file.id,
            file_type: fileType.toString(),
            file_size: file.fileSize.toString(),
            file_extension: file.fileExtension,
            file_name: file.filename,
            state: constants.CHAT.STATES.SENDING,
          }

          // If marked as a user voice recording via FilePond metadata and it's an audio file, compute audio volume profile
          const metadataFromPond = typeof file.getMetadata === 'function' ? (file.getMetadata() || {}) : {}
          const isVoiceRecording = metadataFromPond.voice_recording === true
          if (fileType === constants.LEARNINGMATERIALS.TYPE_AUDIO && file?.file) {
            try {
              if (isVoiceRecording) {
                const volumes = await extractVolumeProfileFromBlob(file.file, 100)
                metadata.audio_volumes = JSON.stringify(volumes)
                metadata.voice_recording = 'true'
              }
              const duration = await extractAudioDurationFromBlob(file.file)
              metadata.audio_duration_seconds = String(duration)
            } catch (e) {
              // Ignore duration extraction errors; playback still works
            }
          }

          // Attach transcript if provided via metadata
          if (metadataFromPond.transcript) {
            metadata.transcript = metadataFromPond.transcript
            try {
              const detected = await detectLanguage(metadataFromPond.transcript)
              if (Array.isArray(detected) && detected.length > 0 && detected[0]?.languageCode) {
                metadata.language = detected[0].languageCode
              }
            } catch (e) {
              // Its fine to ignore detection errors. The only consequence will be that the other user cant translate this message.
              // eslint-disable-next-line no-console
              console.error(e)
            }
          }

          if (fileType === constants.LEARNINGMATERIALS.TYPE_DOCUMENT_IMAGE && file.file) {
            try {
              const { base64, width, height } = await getImageThumbBase64(file.file)
              metadata.preview_base64 = base64
              metadata.image_width = `${width}`
              metadata.image_height = `${height}`
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error('Failed to generate image preview', e)
            }
          }
          const sendMessageResult = await azureChatApi.sendMessage(
            azureChatThreadId,
            getI18n().t('chat.uploading'),
            metadata,
          )
          this.attachments[file.id] = sendMessageResult.id
          let fileUrl = file
          if (fileType === constants.LEARNINGMATERIALS.TYPE_DOCUMENT_IMAGE || fileType === constants.LEARNINGMATERIALS.TYPE_AUDIO) {
            fileUrl = URL.createObjectURL(file.file)
          }
          chatThreadStore.tempFilesMessages[sendMessageResult.id] = fileUrl
          await nextTick()
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e)
          this.isUploading = false
        }
      },
      async saveAttachmentFile(serverId, file = null) {
        if (file) {
          this.attachments[serverId] = this.attachments[file.id]
          delete this.attachments[file.id]
          this.updateAttachmentsStorage()
        }

        relaunchApi.post('/chat/filepond/api/save-file', { serverId, chatThreadId: this.chatThread.id })
          .then(async (response) => {
            const messageId = this.attachments[serverId]

            if (!messageId) {
              this.updateAttachmentsStorage()
              return
            }

            const updatedMessage = chatThreadStore.updateMessageByAzureId(messageId, {
              content: response.file_url,
              metadata: {
                file_id: serverId,
                type: 'attachment',
                error_status: undefined,
                uploadedPercentage: undefined,
                uploading: 'false',
                state: constants.CHAT.STATES.SENT,
              },
            })

            if (updatedMessage) {
              await azureChatApi.updateMessage(
                azureChatThreadId,
                messageId,
                {
                  content: response.file_url,
                  metadata: updatedMessage.metadata,
                },
              )
              chatApi.notifyAboutNewMessage({
                body: response.file_url,
                chatThreadId: chatStore.chatThread.id,
                participantId: getParticipant(chatStore.chatThread)?.user_id,
                messageId,
                metadata: updatedMessage.metadata,
              })
            }
            if (this.attachments[serverId]) {
              delete this.attachments[serverId]
            }
            this.updateAttachmentsStorage()
          })
          .catch(async (e) => {
            console.error(e)
            await this.handleFailureWhenUploading(serverId, constants.CHAT.MESSAGE_ERRORS.FAILED_TO_SAVE)
          }).finally(() => {
            this.isUploading = false
          })
      },
      updateAttachmentUploadingProgress(file, progress) {
        return new Promise((resolve, reject) => {
          const messageId = this.attachments[file.id]
          if (!messageId) {
            reject()
            return
          }

          const updatedMessage = chatThreadStore.updateMessageByAzureId(messageId, {
            metadata: {
              state: constants.CHAT.STATES.SENDING,
              uploadedPercentage: progress ? (parseFloat(progress) / 100) : undefined,
              uploading: 'true',
            },
          })

          if (updatedMessage) {
            resolve()
            return
          }
          reject()
        })
      },
      getFileIdByMessageId(messageId) {
        const storedAttachments = this.attachments || {}
        for (const [fileId, mappedMessageId] of Object.entries(storedAttachments)) {
          if (String(mappedMessageId) === String(messageId)) {
            return fileId
          }
        }
        return null
      },
      updateAttachmentsStorage() {
        if (!Object.entries(this.attachments).length) {
          localStorage.removeItem('chat_attachments')
          return
        }
        localStorage.setItem('chat_attachments', JSON.stringify(this.attachments))
      },
      async getMessagesByType(type, page = 1) {
        this.loadingMessages = true
        const { data, total } = await chatApi.getMessagesByType(chatStore.chatThread.id, type, page)
        this.loadingMessages = false
        return { data, total }
      },
      /**
       * Cancel an in-progress upload or delete a failed upload message.
       * - Aborts the FilePond upload if still in progress
       * - Deletes the message from Azure (removes for both sender and receiver)
       * - Cleans up local state (attachments mapping, tempFilesMessages)
       * @param {string} fileId - The FilePond file.id used as key in attachments
       * @param {number|string} messageId - The Azure message ID to delete
       */
      async cancelUploadOrDeleteMessage(fileId, messageId) {
        if (!messageId) {
          return
        }

        // Check if message still exists and is in a cancellable state
        const message = chatThreadStore.messages.find((m) => Number(m.id) === Number(messageId))
        if (!message) {
          return
        }

        // Guard: Do not delete if message already transitioned to successful sent state
        const messageState = message.metadata?.state
        if (messageState === constants.CHAT.STATES.SENT) {
          return
        }

        // 1. Abort FilePond upload if still in progress
        if (chatStore.fileUploadRef) {
          try {
            const pond = chatStore.fileUploadRef.getFilePond()
            if (pond) {
              const files = pond.getFiles()
              const fileToRemove = files.find((f) => f.id === fileId)
              if (fileToRemove) {
                pond.removeFile(fileToRemove.id)
              }
            }
          } catch (e) {
            // Ignore errors when removing file from FilePond
          }
        }

        // 2. Delete message from Azure (removes for both sender and receiver)
        try {
          await azureChatApi.deleteMessage(azureChatThreadId, messageId)
        } catch (e) {
          // Continue with local cleanup even if remote delete fails
        }

        // 3. Remove message from local state
        const messageIndex = chatThreadStore.messages.findIndex((m) => Number(m.id) === Number(messageId))
        if (messageIndex !== -1) {
          chatThreadStore.messages.splice(messageIndex, 1)
          chatThreadStore.updateSortedMessages()
        }

        // 4. Remove from allLoadedMessages cache
        const cacheIndex = chatThreadStore.allLoadedMessages.findIndex((m) => Number(m.id) === Number(messageId))
        if (cacheIndex !== -1) {
          chatThreadStore.allLoadedMessages.splice(cacheIndex, 1)
        }

        // 5. Clean up tempFilesMessages
        if (chatThreadStore.tempFilesMessages[messageId]) {
          delete chatThreadStore.tempFilesMessages[messageId]
        }

        // 6. Clean up attachments mapping
        if (fileId && this.attachments[fileId]) {
          delete this.attachments[fileId]
          this.updateAttachmentsStorage()
        }

        // 7. Reset uploading state
        this.isUploading = false

        // Refresh file upload component
        chatStore.fileUploadKey = new Date().valueOf()
      },
    },

  })

  return store()
}
